import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { translate, getBrowserLanguage, isRTL, LANGUAGES } from '../i18n/i18n';

/**
 * Language Context
 * Manages application language with localStorage persistence
 */

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: () => '',
  languages: LANGUAGES,
});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Initialize language from localStorage or browser preference
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('pray4all-language');
    if (saved && LANGUAGES[saved]) {
      return saved;
    }
    return getBrowserLanguage();
  });

  // Save to localStorage whenever language changes
  useEffect(() => {
    localStorage.setItem('pray4all-language', language);

    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;

    // Update dir attribute for RTL support
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
  }, [language]);

  // Memoized translation function
  const t = useCallback(
    (key, variables) => {
      return translate(key, language, variables);
    },
    [language]
  );

  // Set language function
  const setLanguage = useCallback((newLang) => {
    if (LANGUAGES[newLang]) {
      setLanguageState(newLang);
    } else {
      console.error(`Language "${newLang}" is not supported`);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      languages: LANGUAGES,
      isRTL: isRTL(language),
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
